import { CrescLoader } from "./LoadingScreen";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Sun, Moon, Upload, Link as LinkIcon, UserPlus, Check, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useEntitlements } from "../context/EntitlementsContext";
import { confirmDelete } from "./ConfirmDialog";
import ShorthandManager from "./ShorthandManager";

// Presets used by the Weekday Tints section. Days are ISO 1-7 (Mon=1..Sun=7);
// swatches are subtle background hints that DayColumn respects.
const WEEKDAY_TINT_PRESETS = {
  days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  swatches: ["", "#0055FF", "#A855F7", "#00C781", "#FFCC00", "#FF8A00", "#FF3B30", "#14B8A6", "#6366F1"] };

function BillingTab({ onClose }) {
  const nav = useNavigate();
  const ent = useEntitlements();
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/payment-history").then(({ data }) => setTxs(data || [])).catch(() => setTxs([]));
  }, []);

  const cancel = async () => {
    const ok = await confirmDelete({
      title: "Cancel subscription?",
      message: "You'll be moved to the Free plan immediately. Your data is preserved.",
      confirmLabel: "Cancel plan" });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/subscriptions/cancel");
      await ent.reload();
      toast.success("Subscription cancelled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="settings-billing-tab">
      <div className="border border-[#2A2A2A] rounded-md p-4 bg-[#0A0A0A]">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="meta-label">Current plan</div>
            <div className="font-display text-lg font-semibold text-[#EDEDED]">{ent.plan_name || "Free"}</div>
            {ent.seats > 1 && <div className="text-xs text-[#A1A1AA]">{ent.seats} seats</div>}
          </div>
          <div className="flex flex-col gap-1.5">
            <button
              onClick={() => { onClose?.(); nav("/pricing"); }}
              className="px-3 py-1.5 rounded-md bg-[#5A90F5] text-white text-xs font-medium hover:bg-[#4a80e5]"
              data-testid="settings-billing-upgrade"
            >
              {ent.plan === "free" ? "Upgrade plan" : "Change plan"}
            </button>
            {ent.plan !== "free" && (
              <button
                disabled={busy}
                onClick={cancel}
                className="px-3 py-1.5 rounded-md border border-[#FF3B30]/40 text-[#FF3B30] text-xs hover:bg-[#FF3B30]/10 disabled:opacity-50"
                data-testid="settings-billing-cancel"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
        {ent.renewed_at && (
          <div className="text-[11px] text-[#A1A1AA] mt-2">Last renewed {new Date(ent.renewed_at).toLocaleDateString()}</div>
        )}
      </div>

      <div className="border border-[#2A2A2A] rounded-md overflow-hidden">
        <div className="bg-[#1a1a1a] px-3 py-2 border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#A1A1AA] flex items-center justify-between">
          <span>Payment history</span>
          <span>{txs.length} {txs.length === 1 ? "entry" : "entries"}</span>
        </div>
        {txs.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-[#A1A1AA]">No invoices yet.</div>
        ) : (
          <div className="divide-y divide-[#2A2A2A]" data-testid="settings-billing-history">
            {txs.map((t) => (
              <div key={t.session_id} className="px-3 py-2 text-xs flex items-center justify-between" data-testid={`billing-tx-${t.session_id}`}>
                <div>
                  <div className="text-[#EDEDED]">{t.plan_id?.toUpperCase()} — ${t.amount?.toFixed(2)}</div>
                  <div className="text-[10px] text-[#A1A1AA]">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider ${t.status === "completed" ? "text-[#34C759]" : t.status === "expired" ? "text-[#FF3B30]" : "text-[#A1A1AA]"}`}>
                  {t.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[10px] text-[#52525B]">
        Need a refund or have an invoice question? Email <a href="mailto:billing@crescendo.dev" className="text-[#5A90F5] hover:underline">billing@crescendo.dev</a>.
      </p>
    </div>
  );
}

export default function SettingsDialog({ open, onClose, initialTab = "account" }) {
  const { user, setUser, accounts, switchAccount, removeAccount } = useAuth();
  const { theme, setTheme, clock, setClock, snapToGrid, setSnapToGrid, colorBlind, setColorBlind, maxNotifications, setMaxNotifications, weekdayTints, setWeekdayTints, weekStartsOn, setWeekStartsOn } = useSettings();
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [picture, setPicture] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [urlInputOpen, setUrlInputOpen] = useState(false);
  const [urlInputValue, setUrlInputValue] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { setTab(initialTab); }, [initialTab, open]);
  useEffect(() => {
    if (!open) return;
    setName(user?.name || ""); setUsername(user?.username || ""); setEmail(user?.email || "");
    setPicture(user?.picture || ""); setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    setUrlInputOpen(false); setUrlInputValue("");
  }, [open, user]);

  const saveAccount = async () => {
    if (newPassword && newPassword !== confirmPassword) {
      return toast.error("Passwords do not match");
    }
    setSaving(true);
    try {
      const payload = { name, username, email, picture };
      if (newPassword) { payload.password = newPassword; payload.current_password = currentPassword; }
      const { data } = await api.put("/auth/me", payload);
      setUser(data);
      toast.success("Profile updated");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Image too large (max 2MB)");
    const reader = new FileReader();
    reader.onload = () => setPicture(reader.result);
    reader.readAsDataURL(f);
  };

  const applyUrl = () => {
    if (!urlInputValue.trim()) return;
    setPicture(urlInputValue.trim());
    setUrlInputValue(""); setUrlInputOpen(false);
  };

  const tabs = [["account", "Account"], ["appearance", "Appearance"], ["preferences", "Preferences"], ["shorthands", "Shorthands"], ["billing", "Billing"]];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#141414] border-[#2A2A2A] text-[#EDEDED] max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Settings</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Manage your account and preferences.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-4 h-[60vh]">
          <div className="w-40 space-y-1 border-r border-[#2A2A2A] pr-2">
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`w-full text-left px-3 py-2 rounded text-sm ${tab === k ? "bg-[#1a1a1a]" : "text-[#A1A1AA] hover:text-[#EDEDED]"}`}
                data-testid={`settings-tab-${k}`}
              >{l}</button>
            ))}
          </div>
          {/* User request: Account tab content was resizing the whole card —
              fix by constraining the inner pane to the dialog height and
              letting the active tab body scroll. */}
          <div className="flex-1 space-y-4 overflow-y-auto pr-2" data-testid="settings-tab-content">
            {tab === "account" && (
              <>
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-full bg-[#0A0A0A] border border-[#2A2A2A] overflow-hidden flex items-center justify-center flex-shrink-0">
                    {picture ? <img src={picture} alt="" className="w-full h-full object-cover" /> : <span className="font-display text-2xl">{(username || name)?.[0]?.toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="meta-label">PROFILE PICTURE</div>
                    <div className="flex gap-2">
                      <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid="settings-picture-file" />
                      <button onClick={() => fileRef.current?.click()}
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md text-sm hover:bg-[#1a1a1a]"
                        data-testid="upload-file-btn">
                        <Upload className="w-3.5 h-3.5" /> Upload
                      </button>
                      <button onClick={() => setUrlInputOpen(!urlInputOpen)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md text-sm hover:bg-[#1a1a1a]"
                        data-testid="upload-url-btn">
                        <LinkIcon className="w-3.5 h-3.5" /> From URL
                      </button>
                      {picture && (
                        <button onClick={() => setPicture("")} className="px-3 py-1.5 text-sm text-[#A1A1AA] hover:text-[#FF3B30]">Remove</button>
                      )}
                    </div>
                    {urlInputOpen && (
                      <div className="flex gap-2">
                        <input value={urlInputValue} onChange={(e) => setUrlInputValue(e.target.value)} placeholder="https://..."
                          className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-1.5 text-sm outline-none focus:border-[#555]"
                          data-testid="picture-url-input" />
                        <button onClick={applyUrl} className="bg-[#EDEDED] text-[#0A0A0A] px-3 py-1.5 rounded-md text-sm font-medium">Apply</button>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="meta-label block mb-1">USERNAME</label>
                  <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. @ada"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                    data-testid="settings-username-input" />
                </div>
                <div>
                  <label className="meta-label block mb-1">DISPLAY NAME</label>
                  <input value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                    data-testid="settings-name-input" />
                </div>
                <div>
                  <label className="meta-label block mb-1">EMAIL</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                    data-testid="settings-email-input" />
                </div>
                {user?.auth_provider !== "google" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="meta-label block mb-1">CURRENT PASSWORD</label>
                        <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]" />
                      </div>
                      <div>
                        <label className="meta-label block mb-1">NEW PASSWORD</label>
                        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]" />
                      </div>
                    </div>
                    {newPassword && (
                      <div>
                        <label className="meta-label block mb-1">CONFIRM NEW PASSWORD</label>
                        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#555]"
                          data-testid="confirm-password" />
                        {confirmPassword && confirmPassword !== newPassword && (
                          <div className="text-[10px] text-[#FF3B30] mt-1">Passwords do not match</div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Connected accounts — each is a separate stored account */}
                <div className="pt-2 border-t border-[#2A2A2A]">
                  <div className="meta-label mb-2">CONNECTED ACCOUNTS</div>
                  <div className="space-y-1">
                    {accounts.map((a) => {
                      const isActive = a.user_id === user?.user_id;
                      return (
                        <div key={a.user_id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded border border-[#2A2A2A] bg-[#0A0A0A]"
                          data-testid={`connected-account-${a.user_id}`}
                        >
                          <div className="w-7 h-7 rounded-full bg-[#141414] border border-[#2A2A2A] overflow-hidden flex items-center justify-center flex-shrink-0">
                            {a.picture ? <img src={a.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px]">{(a.username || a.name)?.[0]?.toUpperCase()}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{a.username || a.name}</div>
                            <div className="text-[10px] text-[#A1A1AA] truncate">{a.email}</div>
                          </div>
                          {isActive ? (
                            <span className="text-[10px] text-[#00C781] flex items-center gap-1"><Check className="w-3 h-3" /> Active</span>
                          ) : (
                            <button onClick={() => { switchAccount(a.user_id); onClose(); }}
                              className="text-[10px] px-2 py-1 rounded bg-[#1a1a1a] hover:bg-[#2a2a2a]"
                              data-testid={`switch-account-${a.user_id}`}
                            >Switch</button>
                          )}
                          <button onClick={() => { removeAccount(a.user_id); /* silent: account removed */ }}
                            className="text-[10px] text-[#A1A1AA] hover:text-[#FF3B30] p-1"
                            data-testid={`remove-account-${a.user_id}`}
                            title="Remove from this device"
                          ><LogOut className="w-3.5 h-3.5" /></button>
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={() => { onClose(); navigate("/login"); }}
                    className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded border border-dashed border-[#3A3A3A] text-xs text-[#A1A1AA] hover:bg-[#1a1a1a] hover:text-[#EDEDED]"
                    data-testid="add-another-account-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Add another account
                  </button>
                </div>
              </>
            )}
            {tab === "appearance" && (
              <div className="space-y-6">
                <div>
                  <div className="meta-label mb-2">THEME</div>
                  <div className="flex gap-2">
                    {[["dark", Moon, "Dark"], ["light", Sun, "Light"]].map(([k, Icon, l]) => (
                      <button key={k} onClick={() => setTheme(k)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded border ${theme === k ? "border-[#EDEDED] bg-[#1a1a1a]" : "border-[#2A2A2A] text-[#A1A1AA]"}`}
                        data-testid={`settings-theme-${k}`}
                      >
                        <Icon className="w-4 h-4" /> {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {tab === "preferences" && (
              <>
                <div>
                  <div className="meta-label mb-2">COLOR-BLIND SAFE PALETTE</div>
                  <button onClick={() => setColorBlind(!colorBlind)}
                    className={`w-full py-3 rounded border text-sm ${colorBlind ? "border-[#EDEDED] bg-[#1a1a1a]" : "border-[#2A2A2A] text-[#A1A1AA]"}`}
                    data-testid="settings-cb"
                  >{colorBlind ? "On" : "Off"}</button>
                </div>
                <div>
                  <div className="meta-label mb-2">MAX NOTIFICATIONS ON SCREEN</div>
                  <div className="flex gap-2" data-testid="settings-max-notifs">
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} onClick={() => setMaxNotifications(n)}
                        className={`flex-1 py-3 rounded border text-sm ${maxNotifications === n ? "border-[#EDEDED] bg-[#1a1a1a]" : "border-[#2A2A2A] text-[#A1A1AA]"}`}
                        data-testid={`settings-max-notifs-${n}`}
                      >{n}</button>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#A1A1AA] mt-2">How many toast notifications can appear at once.</p>
                </div>
                <p className="text-[11px] text-[#52525B] pt-2 border-t border-[#2A2A2A]">
                  Time format, week start, timezone, background, and zoom are now in{" "}
                  <span className="text-[#5A90F5]">Workspace Settings</span>{" "}
                  (the ⚙ icon in the calendar header).
                </p>
              </>
            )}

            {tab === "shorthands" && (
              <ShorthandManager />
            )}
            {tab === "billing" && (
              <BillingTab onClose={onClose} />
            )}
          </div>
        </div>
        <DialogFooter className="justify-end">
          {tab === "account" && (
            <button disabled={saving} onClick={saveAccount}
              className="bg-[#EDEDED] text-[#0A0A0A] px-4 py-2 rounded-md font-medium hover:bg-white disabled:opacity-60 flex items-center gap-2"
              data-testid="settings-save-btn">
              {saving && <CrescLoader size={16} />} Save changes
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
